import { RefObject } from "react";
import { useShallow } from "zustand/react/shallow";

import TableCellEmpty from "ds/components/Table/Row/CellEmpty";
import { selectVisibleColumns } from "ds/components/Table/store/useTableStore/selectors";
import TableSimpleCell from "ds/components/TableSimple/Cell";
import { TableSimpleStore } from "ds/components/TableSimple/types";
import { encodeApiKeyId } from "shared/ApiKey/urlHelpers";

import SpaceDetailsAccessNameCell from "../components/AccessCells/NameCell";
import SpaceDetailsAccessOriginCell from "../components/AccessCells/OriginCell";
import SpaceDetailsAccessRoleCell from "../components/AccessCells/RoleCell";
import SpaceDetailsAccessRoleSummaryCell from "../components/AccessCells/RoleSummaryCell";
import { SpaceDetailsAccessSessionRoleBinding } from "../components/AccessCells/types";
import SpaceDetailsExpandableStickyRows, {
  ExpandableStickyRowsContext,
} from "../components/ExpandableStickyRows";
import { SPACE_API_KEY_SESSIONS_COLUMN_ID } from "./constants";
import { SpaceDetailsApiKeySession } from "./types";

type SpaceDetailsApiKeySessionsRowsProps = {
  data: SpaceDetailsApiKeySession[];
  store: TableSimpleStore;
  tableRef: RefObject<HTMLDivElement | null>;
};

const getApiKeyId = (apiKey: SpaceDetailsApiKeySession) => apiKey.id;
const getApiKeyRoleBindings = (apiKey: SpaceDetailsApiKeySession) => apiKey.roleBindings;
const getRoleBindingId = (binding: SpaceDetailsAccessSessionRoleBinding) => binding.id;

const SpaceDetailsApiKeySessionsRows = ({
  data,
  store,
  tableRef,
}: SpaceDetailsApiKeySessionsRowsProps) => {
  const columns = store(useShallow(selectVisibleColumns));

  const renderParentCells = (
    apiKey: SpaceDetailsApiKeySession,
    { isExpandable, isExpanded, onToggle }: ExpandableStickyRowsContext
  ) => {
    const singleBinding = apiKey.roleBindings[0];

    return columns.map((column, i) => {
      const cellId = `${apiKey.id}-${column.id}`;
      const cellLayoutProps = { id: cellId, isFirst: i === 0 };

      switch (column.id) {
        case SPACE_API_KEY_SESSIONS_COLUMN_ID.NAME:
          return (
            <SpaceDetailsAccessNameCell
              key={cellId}
              id={cellId}
              name={apiKey.name}
              href={`/settings/api-key/${encodeApiKeyId(apiKey.id)}`}
              isExpandable={isExpandable}
              isExpanded={isExpanded}
              onToggle={onToggle}
            />
          );
        case SPACE_API_KEY_SESSIONS_COLUMN_ID.ROLE:
          if (isExpandable) {
            return (
              <SpaceDetailsAccessRoleSummaryCell key={cellId} {...cellLayoutProps} item={apiKey} />
            );
          }
          if (singleBinding) {
            return (
              <SpaceDetailsAccessRoleCell key={cellId} {...cellLayoutProps} item={singleBinding} />
            );
          }
          return <TableSimpleCell key={cellId} {...cellLayoutProps} />;
        case SPACE_API_KEY_SESSIONS_COLUMN_ID.ORIGIN:
          // Same for every row, so the parent needs no summary variant.
          return <SpaceDetailsAccessOriginCell key={cellId} {...cellLayoutProps} />;
        default:
          return null;
      }
    });
  };

  const renderChildCells = (
    binding: SpaceDetailsAccessSessionRoleBinding,
    apiKey: SpaceDetailsApiKeySession
  ) =>
    columns.map((column, i) => {
      const cellId = `${apiKey.id}-${binding.id}-${column.id}`;
      const cellLayoutProps = { id: cellId, isFirst: i === 0 };

      switch (column.id) {
        case SPACE_API_KEY_SESSIONS_COLUMN_ID.ROLE:
          return <SpaceDetailsAccessRoleCell key={cellId} {...cellLayoutProps} item={binding} />;
        case SPACE_API_KEY_SESSIONS_COLUMN_ID.ORIGIN:
          return <SpaceDetailsAccessOriginCell key={cellId} {...cellLayoutProps} />;
        default:
          // Columns without a per-binding value (the name column) show a dash; the connector
          // line is drawn on the child row's first cell via CSS, whichever column that is.
          return (
            <TableSimpleCell key={cellId} id={cellId} role={i === 0 ? "rowheader" : undefined}>
              <TableCellEmpty />
            </TableSimpleCell>
          );
      }
    });

  return (
    <SpaceDetailsExpandableStickyRows
      data={data}
      tableRef={tableRef}
      getParentId={getApiKeyId}
      getChildren={getApiKeyRoleBindings}
      getChildId={getRoleBindingId}
      renderParentCells={renderParentCells}
      renderChildCells={renderChildCells}
    />
  );
};

export default SpaceDetailsApiKeySessionsRows;
